import { ExpandMoreRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  FormControl,
  Grid,
  Grow,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  Slide,
  Tooltip,
  useMediaQuery,
  useTheme,
  Zoom,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {},
  action: {
    marginBottom: theme.spacing(1),
    "& + &": {
      marginLeft: theme.spacing(1),
    },
  },
  appLogo: {
    width: theme.spacing(10),
    height: theme.spacing(10),
    [theme.breakpoints.down("sm")]: {
      width: theme.spacing(6),
      height: theme.spacing(6),
    },
  },
  baseHeaderActions: {
    "& .MuiButton-label": {
      fontSize: "0.70rem",
    },
  },
}));

const GroupButtonMenu = ({ label, items, className }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const [menuState, setMenuState] = React.useState({
    currentTargetElement: null,
    currentActionMenu: null,
    currentElementId: null,
  });

  const handleActionClick = (e, actionMenu) => {
    setMenuState({
      ...menuState,
      currentActionMenu: actionMenu,
      currentTargetElement: e.currentTarget,
      currentElementId: e.currentTarget.getAttribute("id"),
    });
  };

  const renderButtonItem = (item, key) => {
    if (!item) return;
    return (
      <Button
        key={`${item.label}-actm-${key}`}
        id={`${item.label}-actm-${key}`}
        aria-controls={`${item.label}-actm-${key}-menu`}
        aria-haspopup={item.items ? "true" : undefined}
        aria-expanded={
          menuState.currentActionMenu === item ? "true" : undefined
        }
        color={item.color}
        endIcon={
          item.items && item.items.length > 0 ? <ExpandMoreRounded /> : null
        }
        startIcon={item.icon}
        variant={item.variant}
        disabled={item.disabled}
        href={item.href}
        target={item.target}
        onClick={
          !item.href
            ? (e) => {
                if (item.items && item.items.length > 0) {
                  handleActionClick(
                    e,
                    menuState.currentActionMenu === item ? null : item
                  );
                } else {
                  if (item.onClick) {
                    item.onClick(e);
                  }
                }
              }
            : null
        }
        sx={{
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          textTransform: "none",
          fontWeight: 500,
          letterSpacing: 0.5,
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: theme.shadows[4],
          },
          "&:active": {
            transform: "translateY(0px)",
          },
        }}
      >
        {item.label.length > 20 && !isMobile
          ? item.label.substring(0, 17) + "..."
          : item.label}
      </Button>
    );
  };

  const renderMenuItem = (item, key) => {
    if (!item) return;
    var iconColor = `info.main`;
    if (item.color) {
      iconColor = `${item.color}.main`;
    }
    return (
      <MenuItem
        key={`${item.label}-actm-${key}`}
        dense
        value={item}
        onClick={(e) => {
          handleActionClick(e, item);
        }}
        sx={{
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
            transform: "translateX(4px)",
            "& .MuiListItemIcon-root": {
              transform: "scale(1.1)",
            },
          },
        }}
      >
        <ListItemIcon
          color={item.color || "info"}
          sx={{
            transition: "transform 0.2s ease-in-out",
            "& .MuiSvgIcon-root": {
              color: iconColor,
            },
          }}
        >
          {item.icon}
        </ListItemIcon>
        {item.tooltip && item.tooltip !== "" && (
          <ListItemText
            primary={
              <Tooltip title={item.tooltip} placement="right" arrow>
                {item.label}
              </Tooltip>
            }
          />
        )}
        {!item.tooltip && <ListItemText primary={item.label} />}
      </MenuItem>
    );
  };

  if (!items) return null;
  if (isMobile) {
    return (
      <>
        <Slide direction="down" in={true} timeout={400}>
          <Grid
            container
            spacing={1}
            id="base-header-actions"
            className={classes.baseHeaderActions}
          >
            <ButtonGroup size="small">
              {items
                .filter((i) => {
                  if (!i) return false;
                  return i.keepOnMobile;
                })
                .map((m, k) => {
                  return renderButtonItem(m, k);
                })}
            </ButtonGroup>
          </Grid>
        </Slide>

        <Zoom in={true} timeout={500}>
          <FormControl
            fullWidth
            sx={{
              m: 1,
              minWidth: 120,
              "& .MuiOutlinedInput-root": {
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: theme.shadows[2],
                },
                "&.Mui-focused": {
                  boxShadow: theme.shadows[4],
                },
              },
            }}
          >
            <InputLabel htmlFor={`${label}-select-label`}>
              {label ? label : t("Menu")}
            </InputLabel>
            <Select
              labelId={`${label}-select-label`}
              id={`${label}-select`}
              value={"select-action"}
              label={label}
              onChange={(e) => {
                setMenuState({
                  ...menuState,
                  currentActionMenu: e.target.value,
                });
                if (e.target.value.onClick) {
                  e.target.value.onClick(e);
                }
              }}
            >
              <MenuItem value={"select-action"}>{t("Menu")}</MenuItem>
              {items
                .filter((i) => {
                  if (!i) return false;
                  return !i.keepOnMobile;
                })
                .map((m, k) => {
                  if (!m) return;
                  if (m.items) {
                    return [
                      <ListSubheader key={`${m.label}-actm`}>
                        {m.label}
                      </ListSubheader>,
                      ...m.items.map((item, key) => {
                        return renderMenuItem(item, key);
                      }),
                    ];
                  }
                  return renderMenuItem(m, k);
                })}
            </Select>
          </FormControl>
        </Zoom>
      </>
    );
  }
  return (
    <Box mt={2}>
      <Slide direction="right" in={true} timeout={400}>
        <Grid
          container
          spacing={1}
          id="base-header-actions"
          className={classes.baseHeaderActions}
        >
          <ButtonGroup
            variant="outlined"
            size="small"
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{
              boxShadow: theme.shadows[2],
              borderRadius: 1,
              "& .MuiButton-root": {
                borderColor: theme.palette.divider,
              },
            }}
          >
            {items.map((m, k) => {
              if (!m) return;
              return renderButtonItem(m, k);
            })}
          </ButtonGroup>
        </Grid>
      </Slide>
      {menuState.currentTargetElement && (
        <Popper
          sx={{
            zIndex: 1300,
          }}
          open={true}
          anchorEl={
            menuState.currentTargetElement
              ? menuState.currentTargetElement
              : null
          }
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Zoom in={true} timeout={300}>
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === "bottom" ? "left bottom" : "left top",
                }}
              >
                <Paper
                  elevation={8}
                  sx={{
                    overflow: "hidden",
                    borderRadius: 2,
                    backdropFilter: "blur(10px)",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: `1px solid ${theme.palette.divider}`,
                    mt: 1,
                  }}
                >
                  <ClickAwayListener
                    onClickAway={() => {
                      setMenuState({
                        ...menuState,
                        currentTargetElement: null,
                        currentActionMenu: null,
                      });
                    }}
                  >
                    <MenuList
                      id="split-button-menu"
                      autoFocusItem
                      variant="menu"
                      dense
                      sx={{
                        py: 1,
                      }}
                    >
                      {menuState.currentActionMenu?.items.map(
                        (option, index) => {
                          if (!option) return;

                          var iconColor = `info.main`;
                          if (option.color) {
                            iconColor = `${option.color}.main`;
                          }
                          return (
                            <MenuItem
                              key={`${option.label}-actm-${index}`}
                              disabled={option.onClick ? false : true}
                              onClick={option.onClick}
                              sx={{
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                borderRadius: 1,
                                mx: 1,
                                my: 0.5,
                                "&:hover": {
                                  backgroundColor: theme.palette.action.hover,
                                  transform: "translateX(6px) scale(1.02)",
                                  boxShadow: theme.shadows[2],
                                  "& .MuiListItemIcon-root": {
                                    transform: "rotate(5deg) scale(1.15)",
                                  },
                                  "& .MuiListItemText-primary": {
                                    fontWeight: 600,
                                  },
                                },
                              }}
                            >
                              {option.icon && option.icon !== "" && (
                                <ListItemIcon
                                  color={option.color || "info"}
                                  sx={{
                                    transition: "transform 0.2s ease-in-out",
                                    "& .MuiSvgIcon-root": {
                                      color: iconColor,
                                    },
                                  }}
                                >
                                  {option.icon}
                                </ListItemIcon>
                              )}
                              <Tooltip
                                title={
                                  option.tooltip ? option.tooltip : option.label
                                }
                                placement="right"
                                arrow
                                TransitionComponent={Zoom}
                              >
                                <ListItemText
                                  primary={option.label}
                                  sx={{
                                    transition: "font-weight 0.2s ease-in-out",
                                  }}
                                />
                              </Tooltip>
                            </MenuItem>
                          );
                        }
                      )}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            </Zoom>
          )}
        </Popper>
      )}
    </Box>
  );
};

const ButtonMenuItem = ({ item }) => {
  return (
    <MenuItem
      key={`${item.label}-actm`}
      dense
      value={item}
      onClick={item.onClick}
    >
      {item.icon} {item.label}
    </MenuItem>
  );
};

const menuItemProps = {
  label: PropTypes.string,
  icon: PropTypes.element,
  onClick: PropTypes.func,
  keepOnMobile: PropTypes.bool,
  disabled: PropTypes.bool,
  color: PropTypes.oneOf([
    "default",
    "inherit",
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
  ]),
  variant: PropTypes.oneOf(["text", "outlined", "contained"]),
};
ButtonMenuItem.propTypes = {
  item: PropTypes.shape(menuItemProps),
};

ButtonMenuItem.defaultProps = {
  item: {
    label: "Menu Item",
    icon: null,
    onClick: () => {},
    keepOnMobile: false,
    color: "primary",
    variant: "outlined",
    disabled: false,
  },
};

GroupButtonMenu.propTypes = {
  label: PropTypes.string,
  items: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        ...menuItemProps,
        items: PropTypes.arrayOf(ButtonMenuItem.propTypes.item),
      })
    ),
    null,
    undefined,
    PropTypes.bool,
  ]),
  className: PropTypes.string,
};

GroupButtonMenu.defaultProps = {
  label: "Menu",
  items: null,
  className: null,
};

export default GroupButtonMenu;

export { ButtonMenuItem };
